'use client';

import {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
} from 'react';

// Define types locally to avoid import issues during build
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SophonzSDKConfig = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Sophonz = any;

// Define the type for Sophonz configuration
// interface SophonzConfig {
//   collectorUrl: string;
//   appName: string;
//   appVersion: string;
//   appKey: string;
//   consoleCapture?: boolean;
//   advancedNetworkCapture?: boolean;
//   disableReplay?: boolean;
//   captureMetrics?: boolean;
//   debug?: boolean;
//   samplingProbability?: number;
// }

// Define WebView parameters interface
interface WebViewParams {
  isWebView: boolean;
  sessionId: string;
  appName: string;
  appKey: string;
  appVersion: string;
  collectorUrl: string;
  project?: string;
  deploymentEnvironment?: string;
  isInstrumented: boolean;
  isDeactivated: boolean;
}

// Define the shape of the context
interface SophonzContextType {
  config: SophonzSDKConfig;
  isInitialized: boolean;
  webViewParams: WebViewParams;
  updateConfig: (newConfig: Partial<SophonzSDKConfig>) => void;
  initSophonz: () => void;
  deinitSophonz: () => void;
  toggleFeature: (
    feature: keyof SophonzSDKConfig,
    value: boolean | number | string,
  ) => void;
}

// Create the context with default values
const SophonzContext = createContext<SophonzContextType>({
  config: {
    collectorUrl: '',
    appName: '',
    appVersion: '',
    appKey: '',
    consoleCapture: false,
    advancedNetworkCapture: false,
    disableReplay: true,
    debug: false,
    samplingProbability: 1,
    captureMetrics: true,
  },
  isInitialized: false,
  webViewParams: {
    isWebView: false,
    sessionId: '',
    appName: '',
    appKey: '',
    appVersion: '',
    collectorUrl: '',
    isInstrumented: false,
    isDeactivated: false,
  },
  updateConfig: () => {},
  initSophonz: () => {},
  deinitSophonz: () => {},
  toggleFeature: () => {},
});

// Custom hook to use the Sophonz context
export const useSophonz = () => useContext(SophonzContext);

interface SophonzProviderProps {
  children: ReactNode;
}

// Helper function to get initial config from localStorage
const getInitialConfig = (): SophonzSDKConfig => {
  const defaultConfig: SophonzSDKConfig = {
    collectorUrl: '',
    appName: '',
    appVersion: '',
    appKey: '',
    consoleCapture: false,
    advancedNetworkCapture: false,
    disableReplay: true,
    debug: false,
    samplingProbability: 1,
    captureMetrics: false,
    disablePreflight: false,
    exporterProtocol: 'json',
    sessionStorage: 'cookie',
    // Advanced WebVitals settings
    webVitals: true,
    // Advanced socket settings
    websocket: false,
    socketio: false,
    // Advanced recording settings
    recordCanvas: false,
    maskAllInputs: true,
    maskAllText: false,
    maskClass: '',
    ignoreClass: '',
    blockClass: '',
    ignoreUrls: [],
    tracePropagationTargets: [],
    // Advanced instrumentations settings - sync with main config
    instrumentations: {
      console: true,
      document: true,
      errors: true,
      fetch: true,
      interactions: true,
      longtask: true,
      visibility: true,
      connectivity: true,
      postload: true,
      socketio: false,
      websocket: false,
      webvitals: true, // synced with webVitals
      xhr: true,
    },
  };

  // Only try to read from localStorage if we're in the browser
  if (typeof window !== 'undefined') {
    try {
      const savedConfig = localStorage.getItem('sophonz-config');
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig) as Partial<SophonzSDKConfig>;
        console.log('Loading initial config from localStorage:', parsedConfig);

        // Merge with default config to ensure all properties exist
        const mergedConfig = {
          ...defaultConfig,
          ...parsedConfig,
          instrumentations: {
            ...defaultConfig.instrumentations,
            ...parsedConfig.instrumentations,
          },
        };

        // Sync duplicate settings
        if (parsedConfig.webVitals !== undefined) {
          mergedConfig.instrumentations.webvitals = parsedConfig.webVitals;
        }
        if (parsedConfig.websocket !== undefined) {
          mergedConfig.instrumentations.websocket = parsedConfig.websocket;
        }
        if (parsedConfig.socketio !== undefined) {
          mergedConfig.instrumentations.socketio = parsedConfig.socketio;
        }

        return mergedConfig;
      }
    } catch (error) {
      console.error('Failed to parse saved config, using defaults:', error);
    }
  }

  return defaultConfig;
};

// Provider component to wrap around the application
export const SophonzProvider = ({ children }: SophonzProviderProps) => {
  const [config, setConfig] = useState<SophonzSDKConfig>(getInitialConfig);

  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [Sophonz, setSophonz] = useState<Sophonz | null>(null);
  const [webViewParams, setWebViewParams] = useState<WebViewParams>({
    isWebView: false,
    sessionId: '',
    appName: '',
    appKey: '',
    appVersion: '',
    collectorUrl: '',
    isInstrumented: false,
    isDeactivated: false,
  });

  // Check for WebView parameters and initialize if present
  useEffect(() => {
    const win = window as unknown as {
      __sophonz_session_id?: string;
      __sophonz_app_name?: string;
      __sophonz_app_version?: string;
      __sophonz_app_key?: string;
      __sophonz_collector_url?: string;
      __sophonz_project?: string;
      __sophonz_deployment_environment?: string;
      __sophonz_shared_session?: boolean;
      __sophonz_deactivated?: boolean;
      Sophonz?: { init: (config: SophonzSDKConfig) => void; deinit: () => void };
    };
    const isWebView = typeof win.__sophonz_session_id !== 'undefined';

    if (isWebView) {
      const params = {
        isWebView: true,
        sessionId: win.__sophonz_session_id || '',
        appName: win.__sophonz_app_name || '',
        appKey: win.__sophonz_app_key || '',
        appVersion: win.__sophonz_app_version || '',
        collectorUrl: win.__sophonz_collector_url || '',
        project: win.__sophonz_project,
        deploymentEnvironment: win.__sophonz_deployment_environment,
        isInstrumented: win.__sophonz_shared_session === true,
        isDeactivated: win.__sophonz_deactivated === true,
      };

      setWebViewParams(params);

      // If WebView parameters are present and not deactivated, initialize Sophonz
      if (!params.isDeactivated) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setConfig((prevConfig: any) => ({
          ...prevConfig,
          collectorUrl: params.collectorUrl,
          appName: params.appName,
          appKey: params.appKey,
          appVersion: params.appVersion,
          ...(params.project && { project: params.project }),
          ...(params.deploymentEnvironment && { deploymentEnvironment: params.deploymentEnvironment }),
        }));
      }
    }
  }, []);

  // Load Sophonz module once on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const loadSophonz = async () => {
      try {
        // Import Sophonz dynamically to avoid SSR issues
        const SophonzModule = await import('@sophonz/browser-sdk');
        setSophonz(SophonzModule.default);
        console.log('Sophonz module loaded successfully');
      } catch (error) {
        console.error('Failed to load Sophonz module:', error);
      }
    };

    loadSophonz();
  }, []);

  // Auto-initialize Sophonz when config changes and module is loaded
  useEffect(() => {
    if (!Sophonz || typeof window === 'undefined') return;

    // Check if we have required config values
    const hasRequiredValues = !!(
      config.collectorUrl &&
      config.appName &&
      config.appVersion &&
      config.appKey
    );

    console.log('Config changed - checking for auto-initialization:', {
      collectorUrl: !!config.collectorUrl,
      appName: !!config.appName,
      appVersion: !!config.appVersion,
      appKey: !!config.appKey,
      hasAllRequired: hasRequiredValues,
      currentlyInitialized: isInitialized
    });

    if (hasRequiredValues && !isInitialized) {
      console.log('Auto-initializing Sophonz with config:', config);
      try {
        Sophonz.init(config);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to auto-initialize Sophonz:', error);
      }
    } else if (!hasRequiredValues && isInitialized) {
      console.log('Required config values missing, deinitializing Sophonz');
      try {
        Sophonz.deinit();
        setIsInitialized(false);
      } catch (error) {
        console.error('Failed to deinitialize Sophonz:', error);
      }
    }
  }, [config, Sophonz, isInitialized]);

  // Save config to localStorage whenever it changes (after initial load)
  useEffect(() => {
    // Simple approach: always save after component is mounted
    if (typeof window === 'undefined') return;

    try {
      const configString = JSON.stringify(config);
      localStorage.setItem('sophonz-config', configString);
      console.log('Saved config to localStorage:', {
        config,
        configString: configString.substring(0, 200) + '...'
      });
    } catch (error) {
      console.warn('Failed to save Sophonz config to localStorage:', error);
    }
  }, [config]);

  // Update isInitialized state based on actual Sophonz status
  useEffect(() => {
    if (typeof window === 'undefined' || !Sophonz) return;

    const checkInitializedStatus = () => {
      const actualInitialized = Sophonz.inited === true;
      if (actualInitialized !== isInitialized) {
        console.log('Syncing isInitialized state:', actualInitialized);
        setIsInitialized(actualInitialized);
      }
    };

    // Check immediately
    checkInitializedStatus();

    // Check periodically
    const interval = setInterval(checkInitializedStatus, 1000);
    return () => clearInterval(interval);
  }, [Sophonz, isInitialized]);

  // Update config and save to localStorage
  const updateConfig = (newConfig: Partial<SophonzSDKConfig>) => {
    console.log('updateConfig called with:', newConfig);
    const updatedConfig = { ...config, ...newConfig };

    // Sync duplicate settings between performance and feature toggle sections
    if ('webVitals' in newConfig && updatedConfig.instrumentations) {
      updatedConfig.instrumentations = {
        ...updatedConfig.instrumentations,
        webvitals: typeof updatedConfig.webVitals === 'boolean' ? updatedConfig.webVitals : true
      };
    }

    if ('websocket' in newConfig && updatedConfig.instrumentations) {
      updatedConfig.instrumentations = {
        ...updatedConfig.instrumentations,
        websocket: typeof updatedConfig.websocket === 'boolean' ? updatedConfig.websocket : false
      };
    }

    if ('socketio' in newConfig && updatedConfig.instrumentations) {
      updatedConfig.instrumentations = {
        ...updatedConfig.instrumentations,
        socketio: typeof updatedConfig.socketio === 'boolean' ? updatedConfig.socketio : false
      };
    }

    // Sync instrumentations changes back to main config
    if ('instrumentations' in newConfig && newConfig.instrumentations) {
      if ('webvitals' in newConfig.instrumentations) {
        updatedConfig.webVitals = typeof newConfig.instrumentations.webvitals === 'boolean' ? newConfig.instrumentations.webvitals : true;
      }
      if ('websocket' in newConfig.instrumentations) {
        updatedConfig.websocket = typeof newConfig.instrumentations.websocket === 'boolean' ? newConfig.instrumentations.websocket : false;
      }
      if ('socketio' in newConfig.instrumentations) {
        updatedConfig.socketio = typeof newConfig.instrumentations.socketio === 'boolean' ? newConfig.instrumentations.socketio : false;
      }
    }

    console.log('Final updated config:', updatedConfig);
    setConfig(updatedConfig);

    try {
      const configString = JSON.stringify(updatedConfig);
      localStorage.setItem('sophonz-config', configString);
      console.log('Config updated and saved to localStorage successfully');
    } catch (error) {
      console.error('Failed to save updated config to localStorage:', error);
    }

    // Check if we can initialize Sophonz with the updated config
    const hasRequiredValues = !!(
      updatedConfig.collectorUrl &&
      updatedConfig.appName &&
      updatedConfig.appVersion &&
      updatedConfig.appKey
    );

    console.log('After update - required values check:', {
      collectorUrl: !!updatedConfig.collectorUrl,
      appName: !!updatedConfig.appName,
      appVersion: !!updatedConfig.appVersion,
      appKey: !!updatedConfig.appKey,
      hasAllRequired: hasRequiredValues,
      currentlyInitialized: isInitialized
    });

    if (Sophonz) {
      if (isInitialized) {
        // Already initialized - need to reinitialize
        console.log('Sophonz already initialized, reinitializing with new config...');
        deinitSophonz();
        setTimeout(() => {
          initSophonz();
        }, 100);
      } else if (hasRequiredValues) {
        // Not initialized yet, but now we have all required values
        console.log('Sophonz not initialized yet, initializing with new config...');
        setTimeout(() => {
          initSophonz();
        }, 100);
      }
    }
  };

  // Initialize Sophonz with the current config
  const initSophonz = () => {
    if (!Sophonz) {
      console.warn('Sophonz module not loaded yet');
      return;
    }

    try {
      // Ensure we have required values
      if (
        !config.collectorUrl ||
        !config.appName ||
        !config.appVersion ||
        !config.appKey
      ) {
        console.error('Missing required Sophonz configuration:', {
          collectorUrl: !!config.collectorUrl,
          appName: !!config.appName,
          appVersion: !!config.appVersion,
          appKey: !!config.appKey
        });
        return;
      }

      // Check if already initialized
      if (Sophonz.inited) {
        console.warn('Sophonz already initialized, deinitializing first...');
        Sophonz.deinit();
      }

      // Initialize Sophonz with current config
      Sophonz.init(config);
      setIsInitialized(true);
      console.info('Sophonz initialized successfully with config:', config);
    } catch (error) {
      console.error('Failed to initialize Sophonz:', error);
      setIsInitialized(false);
    }
  };

  // Deinitialize Sophonz
  const deinitSophonz = () => {
    if (!Sophonz) {
      console.warn('Sophonz module not loaded');
      return;
    }

    try {
      if (Sophonz.inited) {
        Sophonz.deinit();
        setIsInitialized(false);
        console.info('Sophonz deinitialized successfully');
      } else {
        console.warn('Sophonz was not initialized');
        setIsInitialized(false);
      }
    } catch (error) {
      console.error('Failed to deinitialize Sophonz:', error);
      setIsInitialized(false);
    }
  };

  // Toggle a feature in the config
  const toggleFeature = (
    feature: keyof SophonzSDKConfig,
    value: boolean | number | string,
  ) => {
    updateConfig({ [feature]: value });
  };

  return (
    <SophonzContext.Provider
      value={{
        config,
        isInitialized,
        webViewParams,
        updateConfig,
        initSophonz,
        deinitSophonz,
        toggleFeature,
      }}
    >
      {children}
    </SophonzContext.Provider>
  );
};
